import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AccountStatus } from '@/types/auth';
import { useAuthStore } from '@/stores/auth';

export function useAuth() {
  const s = useAuthStore();
  return {
    user: s.user,
    isAuthenticated: s.isAuthenticated,
    isAdmin: s.isAdmin,
    isCollector: s.isCollector,
    isApproacher: s.isApproacher,
    isMago: s.isMago,
    login: s.login,
    logout: s.logout,
  };
}

export function useRequireAuth(): void {
  const { isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);
}

export function useRequireAdmin(): void {
  const { isAdmin, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate('/', { replace: true });
    }
  }, [isAdmin, isLoading, navigate]);
}

export function useRequireAdminOrCollector(): void {
  const { isAdmin, isCollector, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAdmin && !isCollector) {
      navigate('/', { replace: true });
    }
  }, [isAdmin, isCollector, isLoading, navigate]);
}

export function useAccountStatus(): AccountStatus | null {
  const user = useAuthStore((s) => s.user);
  return user?.account_status ?? null;
}

export function useRequireNotVerifying(): void {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user?.account_status === 'verifying') {
      navigate('/team/collectors', { replace: true });
    }
  }, [isLoading, user?.account_status, navigate]);
}

// First-visit gate: a user who just uploaded their photo lands in 'test'
// status and must consume both /game-rules videos before navigating away.
export function useIsGameRulesGated(): boolean {
  const user = useAuthStore((s) => s.user);
  return user?.account_status === 'test' && !user.rules_acknowledged_at;
}

export function useRequireNotGameRulesGated(): void {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (pathname === '/game-rules') return;
    const gated =
      user?.account_status === 'test' && !user?.rules_acknowledged_at;
    if (gated) navigate('/game-rules', { replace: true });
  }, [
    isLoading,
    user?.account_status,
    user?.rules_acknowledged_at,
    pathname,
    navigate,
  ]);
}
